import { type ReactNode, useState } from "react";
import {
  AgentsIcon,
  EditIcon,
  FileIcon,
  FlowsIcon,
  TemplatesIcon,
  UpgradeIcon,
} from "@hightouch/htui";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useUser } from "src/contexts/user-context";
import {
  AgenticSettingsEntityType,
  ContextFileType,
  type GetCurrentAgenticSettingsEntitiesQueryVariables,
} from "src/graphql/types";
import type { CreateOption } from "src/pages/context-hub/components/knowledge-list/create-menu-button";
import { useContextAgentPanel } from "src/pages/context-hub/context-agent/context-agent-panel-context";
import { KNOWLEDGE_BASE } from "src/pages/context-hub/components/use-knowledge-section";
import { NEW_DAG_SKILL_PATH } from "src/pages/dag-skills/dag-paths";
import { useDagSkillsEnabled } from "src/pages/dag-skills/use-dag-skills-enabled";
import { useLocation, useNavigate } from "src/router";
import { isHightouchStaffUser } from "src/utils/user";
import { StarterTemplateDialog } from "./starter-template-dialog";
import { CONTEXT_SURFACE, SKILLS_SURFACE } from "./surface";
import { useUploadFileEntity } from "./use-upload-file-entity";

// What "Create with Context Agent" puts in the chat composer, unsent.
const CREATE_FILE_PROMPT = "Help me create a new context file.";

export const SKILLS_QUERY_VARIABLES: GetCurrentAgenticSettingsEntitiesQueryVariables =
  { contextType: ContextFileType.Skill };

/** What the Files list and detail pages query. */
export const filesQueryVariables = (
  includeHightouchManaged: boolean,
): GetCurrentAgenticSettingsEntitiesQueryVariables => ({
  includeHightouchManaged,
  types: [
    AgenticSettingsEntityType.ContextMarkdown,
    AgenticSettingsEntityType.ContextFile,
  ],
  // Skills have their own page; style images are cited from style documents
  // and managed there.
  excludeContextTypes: [ContextFileType.Skill, ContextFileType.StyleImage],
});

/** Staff with workspace context generation on see managed files and the regenerate control. */
export function useShowContextAdminControls(): boolean {
  const { user } = useUser();
  const { pretendImNotAHightouchAdmin, workspaceContextGenerationEnabled } =
    useFlags();
  return (
    isHightouchStaffUser(user?.admin_type, { pretendImNotAHightouchAdmin }) &&
    Boolean(workspaceContextGenerationEnabled)
  );
}

/**
 * Managing global (Hightouch-managed) skills is staff-only; everyone else sees
 * them read-only. Honors "pretend I'm not an admin" so staff can QA the
 * customer view. The managed-skill resolvers enforce it too.
 */
export function useCanManageGlobalSkills(): boolean {
  const { user } = useUser();
  const { pretendImNotAHightouchAdmin } = useFlags();
  return isHightouchStaffUser(user?.admin_type, {
    pretendImNotAHightouchAdmin,
  });
}

/**
 * Location state a create page reads. `startId` is new on every create the
 * user starts, so a fresh draft replaces the old one, while a filter change on
 * the page keeps it. `templateId` is the Files starter template to begin from.
 */
export type NewContextEntityState = { startId?: string; templateId?: string };

export const readNewContextEntityState = (
  state: unknown,
): NewContextEntityState => {
  if (typeof state !== "object" || state === null) return {};
  const { startId, templateId } = state as Record<string, unknown>;
  return {
    startId: typeof startId === "string" ? startId : undefined,
    templateId: typeof templateId === "string" ? templateId : undefined,
  };
};

/** Opens a type's create page, keeping the filters so its rail matches the list. */
export function useOpenNewPage(path: string) {
  const navigate = useNavigate();
  const location = useLocation();
  return (state?: Pick<NewContextEntityState, "templateId">) =>
    navigate(
      { pathname: `${KNOWLEDGE_BASE}/${path}/new`, search: location.search },
      { state: { ...state, startId: crypto.randomUUID() } },
    );
}

type CreateFlow = {
  createOptions: [CreateOption, ...CreateOption[]];
  /** The template picker and upload input. Render them alongside the menu. */
  dialogs: ReactNode;
};

/** The Files create menu, shared by the list and detail pages. */
export function useFileCreateFlow(): CreateFlow {
  const { openPanelWithDraft } = useContextAgentPanel();
  const openNewPage = useOpenNewPage(CONTEXT_SURFACE.section.path);
  const upload = useUploadFileEntity({
    onUploaded: useOpenDetailPage(CONTEXT_SURFACE.section.path),
  });
  const [isTemplateDialogOpen, setIsTemplateDialogOpen] = useState(false);
  return {
    createOptions: [
      {
        label: "Create with Context Agent",
        icon: AgentsIcon,
        onClick: () => openPanelWithDraft(CREATE_FILE_PROMPT),
      },
      {
        label: "Start from a template",
        icon: TemplatesIcon,
        onClick: () => setIsTemplateDialogOpen(true),
      },
      {
        label: "Create from scratch",
        icon: FileIcon,
        onClick: () => openNewPage(),
      },
      {
        label: "Upload file",
        icon: UpgradeIcon,
        onClick: upload.open,
      },
    ],
    dialogs: (
      <>
        {upload.input}
        <StarterTemplateDialog
          isOpen={isTemplateDialogOpen}
          onClose={() => setIsTemplateDialogOpen(false)}
          onSelectTemplate={(template) => {
            setIsTemplateDialogOpen(false);
            openNewPage({ templateId: template.id });
          }}
        />
      </>
    ),
  };
}

/** The Skills create menu, shared by the list and detail pages. */
export function useSkillCreateOptions(): [CreateOption, ...CreateOption[]] {
  const openNewPage = useOpenNewPage(SKILLS_SURFACE.section.path);
  const navigate = useNavigate();
  const dagSkillsEnabled = useDagSkillsEnabled();
  if (!dagSkillsEnabled) {
    return [
      {
        label: "Create from scratch",
        icon: SKILLS_SURFACE.icon,
        onClick: () => openNewPage(),
      },
    ];
  }
  // Two ways to build the same skill: write it out, or lay it out as steps.
  return [
    {
      label: "Write your own",
      icon: EditIcon,
      onClick: () => openNewPage(),
    },
    {
      label: "Use the skill builder",
      icon: FlowsIcon,
      onClick: () => navigate(NEW_DAG_SKILL_PATH),
    },
  ];
}

/** Opens an entity's detail page from a list, keeping the filters for its rail. */
export function useOpenDetailPage(path: string) {
  const navigate = useNavigate();
  const location = useLocation();
  return (id: string) =>
    navigate({
      pathname: `${KNOWLEDGE_BASE}/${path}/${encodeURIComponent(id)}`,
      search: location.search,
    });
}
